/**
 * Hívogatás (Calling) – bulk outreach: list deals by stage, notes, closing date, email per deal.
 * Desktop: table with sortable columns. Mobile: card list matching Kontaktok layout + sort + pagination.
 */

import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getTranslatedName, getCurrentLang, type TranslatedNameRow } from "@/lib/translation-utils";
import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { apiUrl, apiFetch } from "@/lib/api-client";
import { postDealNoteWithOutbox } from "@/lib/post-deal-note-with-outbox";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import type { Deal, Contact, Stage, Pipeline, BoardResponse, ContactsByIdsResponse } from "@shared/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ClosingDatePickerContent } from "@/components/crm/ClosingDatePickerContent";
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Mail,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ExternalLink,
  ChevronDown,
  Phone,
  CalendarClock,
  MessageSquare,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import EmailComposer from "@/components/email/EmailComposer";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDebounce } from "@/hooks/useDebounce";
import { ContactsHubShell } from "@/components/contacts/ContactsHubShell";
import { ContactsCallingFilterPanel } from "@/components/contacts/ContactsCallingFilterPanel";
import Loading from "@/components/Loading";
import { dealIsClosingOverdue, dealClosingDayIso, closingDateDisplayClass } from "@/lib/crm-closing-date";
import { useAuthLite } from "@/context/AuthLite";
import {
  buildUserScopedKey,
  loadSortPrefV1,
  migrateAnonToUserScopedV1,
  saveSortPrefV1,
} from "@/lib/user-scoped-storage";

const DEALS_PAGE_SIZE = 25;
/** Radix Select sentinel: no stage chosen yet */
const CALLING_STAGE_NONE = "__calling_stage_none__";
type SortBy = "title" | "closingDate" | "createdAt";
type SortOrder = "asc" | "desc";

const CALLING_SORT_BY_KEY = "calling-sort-by";
const CALLING_SORT_ORDER_KEY = "calling-sort-order";
const CALLING_SORT_FEATURE_KEY = "calling.sort";
const CALLING_SORT_BY_ALLOWED = ["title", "closingDate", "createdAt"] as const;
const CALLING_SORT_ORDER_ALLOWED = ["asc", "desc"] as const;
const CALLING_STAGE_BY_PIPELINE_KEY = "calling-selected-stage-by-pipeline";
const LEGACY_CALLING_STAGE_KEY = "calling-selected-stage-id";

/** Ha nincs mentett fázis: ezt részesítjük előnyben a sorrend első szakasza helyett (CRM kanonikus név). */
const CALLING_DEFAULT_STAGE_LABEL = "Jóváhagyásra várunk";

function defaultCallingStageIdWhenNoPreference(stages: Stage[]): string | null {
  if (!stages.length) return null;
  const target = CALLING_DEFAULT_STAGE_LABEL.trim().toLowerCase();
  const matches = (label: string) => label.trim().toLowerCase() === target;
  for (const st of stages) {
    const row = st as Stage & TranslatedNameRow;
    const candidates = [
      getTranslatedName(row, "hu"),
      getTranslatedName(row, "sk"),
      getTranslatedName(row, "en"),
      st.name ?? "",
    ];
    if (candidates.some((x) => x && matches(x))) return st.id;
  }
  return stages[0]!.id;
}

function readCallingStageMap(): Record<string, string> {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem(CALLING_STAGE_BY_PIPELINE_KEY) : null;
    if (!raw) return {};
    const p = JSON.parse(raw) as unknown;
    return p && typeof p === "object" && !Array.isArray(p) ? (p as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeCallingStageMap(map: Record<string, string>) {
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem(CALLING_STAGE_BY_PIPELINE_KEY, JSON.stringify(map));
    }
  } catch {
    /* ignore */
  }
}

/** Deal detail path when opening from Calling (Superfaktúra tab). */
function callingDealPath(dealId: string) {
  return `/crm/deals/${dealId}?tab=superfaktura`;
}

function dealContactId(d: Deal): string | undefined {
  const id = d.contactId ?? (d as any).contact_id;
  return id ? String(id) : undefined;
}

type CallingTelSurface = "mobile" | "table";

function logCallingTelClickAndOpenDialer(
  phoneRaw: string,
  opts: { dealId: string; contactId?: string | null; surface: CallingTelSurface }
) {
  const cleaned = phoneRaw.replace(/\s/g, "");
  if (!cleaned) return;
  const body: { dealId: string; surface: CallingTelSurface; contactId?: string } = {
    dealId: opts.dealId,
    surface: opts.surface,
  };
  if (opts.contactId) body.contactId = opts.contactId;
  void apiFetch("/api/crm/calling/log-tel-click", {
    method: "POST",
    keepalive: true,
    body: JSON.stringify(body),
  });
  window.location.href = `tel:${cleaned}`;
}

function getHubScrollEl(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.querySelector('[data-hub-scroll="1"]') as HTMLElement | null;
}

export default function HivogatasPage() {
  const { t, i18n } = useTranslation();
  const lang = getCurrentLang(i18n.language);
  const dateLocale = getDateFnsLocale(i18n.language);
  const qc = useQueryClient();
  const { user } = useAuthLite();

  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const callingPipelineRestoredRef = useRef(false);
  const callingStageRestoredRef = useRef(false);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortBy>("closingDate");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [searchFilter, setSearchFilter] = useState("");
  const [emailDealId, setEmailDealId] = useState<string | null>(null);
  // Client-side filters on current deal page (client-side)
  const [filterCreatedAfter, setFilterCreatedAfter] = useState<string>("");
  const [filterCreatedBefore, setFilterCreatedBefore] = useState<string>("");
  const [filterClosingAfter, setFilterClosingAfter] = useState<string>("");
  const [filterClosingBefore, setFilterClosingBefore] = useState<string>("");
  const [filterName, setFilterName] = useState<string>("");
  const [filterEmail, setFilterEmail] = useState<string>("");
  const [showOverdueClosingOnly, setShowOverdueClosingOnly] = useState(true);
  const [dealOverrides, setDealOverrides] = useState<Record<string, Partial<Deal>>>({});
  const debouncedSearch = useDebounce(searchFilter, 300);
  const debouncedName = useDebounce(filterName, 300);
  const debouncedEmail = useDebounce(filterEmail, 300);

  useEffect(() => {
    if (!showOverdueClosingOnly) return;
    setFilterClosingAfter("");
    setFilterClosingBefore("");
  }, [showOverdueClosingOnly]);

  // When filters change: go back to first page (server-side filtering + pagination).
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    debouncedName,
    debouncedEmail,
    filterCreatedAfter,
    filterCreatedBefore,
    filterClosingAfter,
    filterClosingBefore,
    showOverdueClosingOnly,
  ]);

  const isMobile = useIsMobile();
  const board = useQuery({
    queryKey: queryKeys.crmBoard,
    queryFn: async ({ signal }) => {
      const r = await apiFetch("/api/crm/board", { credentials: "include", signal });
      if (!r.ok) throw new Error("Board betöltése sikertelen");
      return (await r.json()) as BoardResponse;
    },
  });

  const subPipelines = useMemo(() => {
    const pipelines = board.data?.pipelines ?? [];
    return pipelines.filter((p: Pipeline) => ((p as any).parent_pipeline_id ?? (p as any).parentPipelineId) != null) as Pipeline[];
  }, [board.data?.pipelines]);

  const stagesForPipeline = useMemo(() => {
    if (!selectedPipelineId || !board.data?.stages) return [];
    const stages = (board.data.stages as Stage[]).filter(
      (s: Stage) => (s.pipelineId ?? (s as any).pipeline_id) === selectedPipelineId
    );
    stages.sort((a: Stage, b: Stage) => (a.order ?? a.orderIndex ?? 0) - (b.order ?? b.orderIndex ?? 0));
    return stages;
  }, [board.data?.stages, selectedPipelineId]);

  // Restore persisted sort settings (shared across Calling page, mobile+desktop)
  useEffect(() => {
    // 1) Migrate anon → user scoped when user appears
    migrateAnonToUserScopedV1({ userEmail: user?.email, featureKey: CALLING_SORT_FEATURE_KEY });

    // 2) Legacy migration (global keys) → user scoped key (once)
    try {
      if (typeof window !== "undefined") {
        const scopedKey = buildUserScopedKey(user?.email, CALLING_SORT_FEATURE_KEY);
        const already = window.localStorage.getItem(scopedKey);
        if (!already) {
          const legacyBy = window.localStorage.getItem(CALLING_SORT_BY_KEY);
          const legacyOrder = window.localStorage.getItem(CALLING_SORT_ORDER_KEY);
          const okBy =
            legacyBy && (CALLING_SORT_BY_ALLOWED as readonly string[]).includes(legacyBy);
          const okOrder =
            legacyOrder && (CALLING_SORT_ORDER_ALLOWED as readonly string[]).includes(legacyOrder);
          if (okBy && okOrder) {
            saveSortPrefV1(scopedKey, {
              sortBy: legacyBy as SortBy,
              sortOrder: legacyOrder as SortOrder,
            });
          }
        }
      }
    } catch {
      // ignore
    }

    // 3) Load effective preference (user scoped; falls back to anon scope if no user)
    const scopedKey = buildUserScopedKey(user?.email, CALLING_SORT_FEATURE_KEY);
    const pref = loadSortPrefV1<SortBy, SortOrder>(
      scopedKey,
      CALLING_SORT_BY_ALLOWED,
      CALLING_SORT_ORDER_ALLOWED,
      { sortBy: "closingDate", sortOrder: "desc" },
    );
    setSortBy(pref.sortBy);
    setSortOrder(pref.sortOrder);
  }, [user?.email]);

  // Persist sort settings
  useEffect(() => {
    const scopedKey = buildUserScopedKey(user?.email, CALLING_SORT_FEATURE_KEY);
    saveSortPrefV1<SortBy, SortOrder>(scopedKey, { sortBy, sortOrder });
  }, [sortBy, sortOrder, user?.email]);

  // Restore subpipeline from CRM's last selection (same key as CRM uses), once when board is ready
  useEffect(() => {
    if (!board.data?.pipelines || subPipelines.length === 0 || callingPipelineRestoredRef.current) return;
    try {
      const saved = typeof window !== "undefined" ? localStorage.getItem("crm-selected-subpipeline-id") : null;
      if (saved && subPipelines.some((p: Pipeline) => p.id === saved)) {
        setSelectedPipelineId(saved);
      } else {
        setSelectedPipelineId(subPipelines[0]!.id);
      }
    } catch (_) {
      setSelectedPipelineId(subPipelines[0]!.id);
    }
    callingPipelineRestoredRef.current = true;
  }, [board.data?.pipelines, subPipelines]);

  // Restore last selected stage per subpipeline; legacy key; else first stage in order
  useEffect(() => {
    if (!selectedPipelineId || stagesForPipeline.length === 0 || callingStageRestoredRef.current) return;
    try {
      let chosen: string | null = null;
      const map = readCallingStageMap();
      const fromMap = map[selectedPipelineId];
      if (fromMap && stagesForPipeline.some((s: Stage) => s.id === fromMap)) {
        chosen = fromMap;
      }
      if (!chosen && typeof window !== "undefined") {
        const leg = localStorage.getItem(LEGACY_CALLING_STAGE_KEY);
        if (leg && stagesForPipeline.some((s: Stage) => s.id === leg)) chosen = leg;
      }
      if (!chosen) {
        chosen = defaultCallingStageIdWhenNoPreference(stagesForPipeline);
      }
      setSelectedStageId(chosen);
    } finally {
      callingStageRestoredRef.current = true;
    }
  }, [selectedPipelineId, stagesForPipeline]);

  // When pipeline changes: clear stage if it no longer belongs to the new pipeline; reset restore ref so next pipeline can restore
  useEffect(() => {
    if (!selectedPipelineId || !selectedStageId) return;
    const belongs = stagesForPipeline.some((s: Stage) => s.id === selectedStageId);
    if (!belongs) {
      setSelectedStageId(null);
      setPage(1);
      callingStageRestoredRef.current = false;
    }
  }, [selectedPipelineId, selectedStageId, stagesForPipeline]);

  const persistCallingStage = useCallback((pipelineId: string, stageId: string | null) => {
    if (typeof window === "undefined") return;
    try {
      const map = readCallingStageMap();
      if (stageId) {
        map[pipelineId] = stageId;
        writeCallingStageMap(map);
        localStorage.setItem(LEGACY_CALLING_STAGE_KEY, stageId);
      } else {
        delete map[pipelineId];
        writeCallingStageMap(map);
        localStorage.removeItem(LEGACY_CALLING_STAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const dealsQuery = useQuery({
    queryKey: [
      "crm-deals",
      "hivogatas",
      selectedStageId,
      selectedPipelineId,
      page,
      sortBy,
      sortOrder,
      debouncedSearch,
      debouncedName,
      debouncedEmail,
      filterCreatedAfter,
      filterCreatedBefore,
      filterClosingAfter,
      filterClosingBefore,
      showOverdueClosingOnly,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        stageId: selectedStageId!,
        limit: String(DEALS_PAGE_SIZE),
        offset: String((page - 1) * DEALS_PAGE_SIZE),
        sortBy,
        sortOrder,
      });
      if (selectedPipelineId) params.set("pipelineId", selectedPipelineId);
      if (debouncedSearch.trim()) params.set("q", debouncedSearch.trim());
      if (debouncedName.trim()) params.set("contactName", debouncedName.trim());
      if (debouncedEmail.trim()) params.set("contactEmail", debouncedEmail.trim());
      if (filterCreatedAfter) params.set("createdAfter", filterCreatedAfter);
      if (filterCreatedBefore) params.set("createdBefore", filterCreatedBefore);
      if (filterClosingAfter) params.set("closingAfter", filterClosingAfter);
      if (filterClosingBefore) params.set("closingBefore", filterClosingBefore);
      if (showOverdueClosingOnly) params.set("overdueOnly", "1");
      const r = await apiFetch(`/api/crm/deals?${params}`, { credentials: "include" });
      if (!r.ok) throw new Error("Deal lista betöltése sikertelen");
      return r.json() as Promise<{ deals: Deal[]; total: number; hasMore?: boolean }>;
    },
    enabled: !!selectedStageId,
  });

  const deals = useMemo(() => {
    const list = dealsQuery.data?.deals ?? [];
    if (!list.length) return list;
    return list.map((d) => {
      const ov = dealOverrides[d.id];
      return ov ? ({ ...d, ...ov } as Deal) : d;
    });
  }, [dealsQuery.data?.deals, dealOverrides]);
  const totalDeals = dealsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalDeals / DEALS_PAGE_SIZE));

  const scrollKey = useMemo(() => {
    const parts = [
      "calling-scroll-v1",
      selectedPipelineId ?? "",
      selectedStageId ?? "",
      String(page),
      sortBy,
      sortOrder,
      filterCreatedAfter,
      filterCreatedBefore,
      filterClosingAfter,
      filterClosingBefore,
      filterName,
      filterEmail,
      searchFilter,
      showOverdueClosingOnly,
    ];
    return parts.join("|");
  }, [
    selectedPipelineId,
    selectedStageId,
    page,
    sortBy,
    sortOrder,
    filterCreatedAfter,
    filterCreatedBefore,
    filterClosingAfter,
    filterClosingBefore,
    filterName,
    filterEmail,
    searchFilter,
    showOverdueClosingOnly,
  ]);

  const saveScroll = useCallback(() => {
    if (typeof window === "undefined") return;
    const el = getHubScrollEl();
    if (!el) return;
    try {
      sessionStorage.setItem(scrollKey, String(el.scrollTop ?? 0));
    } catch (_) {}
  }, [scrollKey]);

  useEffect(() => {
    if (!isMobile) return;
    if (!selectedStageId) return;
    if (dealsQuery.isLoading) return;
    // Wait a tick for list layout to settle before restoring.
    const tmr = window.setTimeout(() => {
      const el = getHubScrollEl();
      if (!el) return;
      try {
        const raw = sessionStorage.getItem(scrollKey);
        const n = raw ? Number(raw) : 0;
        if (Number.isFinite(n) && n > 0) el.scrollTop = n;
      } catch (_) {}
    }, 0);
    return () => window.clearTimeout(tmr);
  }, [isMobile, selectedStageId, dealsQuery.isLoading, scrollKey]);

  const boardContactIdSet = useMemo(() => {
    const s = new Set<string>();
    for (const c of board.data?.contacts ?? []) s.add(c.id);
    return s;
  }, [board.data?.contacts]);

  const missingCallingContactIds = useMemo(() => {
    const ids = new Set<string>();
    for (const d of deals) {
      const cid = dealContactId(d);
      if (cid && !boardContactIdSet.has(cid)) ids.add(cid);
    }
    return [...ids].sort();
  }, [deals, boardContactIdSet]);

  const callingExtraContactsQuery = useQuery({
    queryKey: ["crm-calling-contacts-by-ids", missingCallingContactIds.join("|")],
    queryFn: async ({ signal }) => {
      const q = new URLSearchParams();
      q.set("ids", missingCallingContactIds.join(","));
      const r = await apiFetch(`/api/crm/contacts/by-ids?${q.toString()}`, { credentials: "include", signal });
      if (!r.ok) throw new Error("Kapcsolatok betöltése sikertelen");
      const j = (await r.json()) as ContactsByIdsResponse;
      return j.contacts ?? [];
    },
    enabled: missingCallingContactIds.length > 0 && !!board.data,
  });

  const contactsMap = useMemo(() => {
    const m = new Map<string, Contact>();
    for (const c of board.data?.contacts ?? []) m.set(c.id, c);
    for (const c of callingExtraContactsQuery.data ?? []) m.set(c.id, c);
    return m;
  }, [board.data?.contacts, callingExtraContactsQuery.data]);

  // Deals are now filtered server-side (to keep pagination correct, avoid empty pages).
  const filteredDeals = deals;

  const emailDeal = emailDealId ? deals.find((d: Deal) => d.id === emailDealId) : null;
  const emailContactCid = emailDeal ? dealContactId(emailDeal) : undefined;
  const emailContact = emailContactCid ? contactsMap.get(emailContactCid) ?? null : null;

  const clearClientFilters = useCallback(() => {
    setSearchFilter("");
    setFilterCreatedAfter("");
    setFilterCreatedBefore("");
    setShowOverdueClosingOnly(true);
    setFilterClosingAfter("");
    setFilterClosingBefore("");
    setFilterName("");
    setFilterEmail("");
  }, []);

  const hasExtraFilters =
    !!searchFilter.trim() ||
    !!filterCreatedAfter ||
    !!filterCreatedBefore ||
    !!filterClosingAfter ||
    !!filterClosingBefore ||
    !!filterName.trim() ||
    !!filterEmail.trim() ||
    showOverdueClosingOnly;

  if (board.isLoading || !board.data) {
    return (
      <>
        {/* ux-consistency: loading/empty C */}
        <Loading className="min-h-[50vh]" />
      </>
    );
  }

  const pipelineStageBelowSearch =
    subPipelines.length > 0 && selectedPipelineId ? (
      <div className="flex flex-col gap-3">
        <div className="min-w-0 w-full">
          <Label htmlFor="calling-subpipeline" className="text-sm font-medium">
            {t("header.subpipelineLabel")}
          </Label>
          <Select
            value={selectedPipelineId}
            onValueChange={(id) => {
              setSelectedPipelineId(id);
              setSelectedStageId(null);
              setPage(1);
              callingStageRestoredRef.current = false;
              try {
                if (typeof window !== "undefined") {
                  localStorage.setItem("crm-selected-subpipeline-id", id);
                }
              } catch (_) {}
            }}
          >
            <SelectTrigger id="calling-subpipeline" className="mt-1.5 h-10 w-full">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {subPipelines.map((p: Pipeline) => (
                <SelectItem key={p.id} value={p.id}>
                  {getTranslatedName(p as any, lang) || p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {stagesForPipeline.length > 0 ? (
          <div className="min-w-0 w-full">
            <Label htmlFor="calling-stage" className="text-sm font-medium">
              {t("calling.stageColumn")}
            </Label>
            <Select
              value={selectedStageId ?? CALLING_STAGE_NONE}
              onValueChange={(v) => {
                const id = v === CALLING_STAGE_NONE ? null : v;
                setSelectedStageId(id);
                setPage(1);
                if (selectedPipelineId) persistCallingStage(selectedPipelineId, id);
              }}
            >
              <SelectTrigger id="calling-stage" className="mt-1.5 h-10 w-full">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CALLING_STAGE_NONE}>—</SelectItem>
                {stagesForPipeline.map((s: Stage) => (
                  <SelectItem key={s.id} value={s.id}>
                    {getTranslatedName(s as any, lang) || s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>
    ) : null;

  const sidebar = (
    <>
      <div className="rounded-md border bg-card p-4">
        <ContactsCallingFilterPanel
          mode="calling"
          searchPlaceholder={t("calling.searchPlaceholder")}
          searchValue={searchFilter}
          onSearchChange={setSearchFilter}
          filterName={filterName}
          filterEmail={filterEmail}
          filterCreatedAfter={filterCreatedAfter}
          filterCreatedBefore={filterCreatedBefore}
          onFilterNameChange={setFilterName}
          onFilterEmailChange={setFilterEmail}
          onFilterCreatedAfterChange={setFilterCreatedAfter}
          onFilterCreatedBeforeChange={setFilterCreatedBefore}
          filterClosingAfter={filterClosingAfter}
          filterClosingBefore={filterClosingBefore}
          onFilterClosingAfterChange={setFilterClosingAfter}
          onFilterClosingBeforeChange={setFilterClosingBefore}
          belowSearch={pipelineStageBelowSearch}
        />
      </div>
      <div className="mt-3 rounded-md border bg-card p-3 hidden md:block">
        <Button
          type="button"
          variant={showOverdueClosingOnly ? "default" : "outline"}
          size="sm"
          className="h-9 w-full"
          title={t("calling.filterOverdueOnlyHint")}
          aria-pressed={showOverdueClosingOnly}
          onClick={() => {
            setShowOverdueClosingOnly((v) => !v);
            setPage(1);
          }}
        >
          <CalendarClock className="h-4 w-4 mr-2 shrink-0" />
          {t("calling.filterOverdueOnly")}
        </Button>
      </div>
      <div className="mt-3 space-y-2 rounded-md border bg-card p-3 md:hidden">
        <span className="text-xs font-medium text-muted-foreground">{t("crm.sortBy")}</span>
        <Select
          value={sortBy}
          onValueChange={(v) => {
            setSortBy(v as SortBy);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="title">{t("calling.deal")}</SelectItem>
            <SelectItem value="closingDate">{t("calling.closingDate")}</SelectItem>
            <SelectItem value="createdAt">{t("calling.createdAt")}</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={sortOrder}
          onValueChange={(v) => {
            setSortOrder(v as SortOrder);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="asc">{t("crm.sortOrderAsc")}</SelectItem>
            <SelectItem value="desc">{t("crm.sortOrderDesc")}</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant={showOverdueClosingOnly ? "default" : "outline"}
          size="sm"
          className="h-9 w-full"
          title={t("calling.filterOverdueOnlyHint")}
          aria-pressed={showOverdueClosingOnly}
          onClick={() => {
            setShowOverdueClosingOnly((v) => !v);
            setPage(1);
          }}
        >
          <CalendarClock className="h-4 w-4 mr-2 shrink-0" />
          {t("calling.filterOverdueOnly")}
        </Button>
      </div>
      <Button variant="outline" className="w-full" type="button" onClick={clearClientFilters}>
        {t("calling.filterClear")}
      </Button>
    </>
  );

  return (
    <>
      <ContactsHubShell
        title={t("calling.title")}
        activeTab="calling"
        sidebar={sidebar}
        mobileFilterFooter={(close) => (
          <Button className="w-full" type="button" onClick={close}>
            {t("calling.filterApply")}
          </Button>
        )}
      >
        <div className="space-y-4">
          {selectedPipelineId && !selectedStageId && stagesForPipeline.length > 0 && (
            <p className="text-muted-foreground">{t("calling.chooseStage")}</p>
          )}

          {selectedStageId && (
            <>
              {!isMobile ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm text-muted-foreground min-w-0">
                    {t("calling.totalDeals", { count: totalDeals })}
                  </div>
                </div>
              ) : null}

          {dealsQuery.isLoading ? (
            <>
              {/* ux-consistency: loading/empty C */}
              <Loading className="min-h-[40vh] py-12" />
            </>
          ) : isMobile ? (
            <div className="space-y-3 min-w-0 pb-[calc(140px+env(safe-area-inset-bottom))]">
              {filteredDeals.length === 0 ? (
                <div className="py-8 text-center space-y-3">
                  <p className="text-muted-foreground">
                    {hasExtraFilters ? t("calling.noMatches") : t("calling.noDeals")}
                  </p>
                  {hasExtraFilters ? (
                    <div className="flex justify-center">
                      <Button variant="outline" size="sm" type="button" onClick={clearClientFilters}>
                        {t("calling.filterClear")}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : (
                filteredDeals.map((deal: Deal) => {
                  const ccid = dealContactId(deal);
                  return (
                    <CallingMobileCard
                      key={deal.id}
                      deal={deal}
                      contact={ccid ? contactsMap.get(ccid) : undefined}
                      onOpenDeal={saveScroll}
                      onEmailClick={() => setEmailDealId(deal.id)}
                    />
                  );
                })
              )}
              <div className="flex items-center justify-between gap-2 pt-1">
                <div className="text-sm text-muted-foreground min-w-0">
                  {t("calling.totalDeals", { count: totalDeals })}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground min-w-[84px] text-center">
                    {t("calling.pageOf", { current: page, total: totalPages })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="w-full rounded-md border overflow-x-auto min-w-0">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 min-w-[240px] w-[260px]"
                        onClick={() => {
                          setSortBy("title");
                          setSortOrder(sortBy === "title" && sortOrder === "asc" ? "desc" : "asc");
                          setPage(1);
                        }}
                      >
                        {t("calling.deal")} {sortBy === "title" && (sortOrder === "asc" ? "↑" : "↓")}
                      </TableHead>
                      <TableHead className="min-w-[150px] w-[160px]">{t("calling.stageColumn")}</TableHead>
                      <TableHead className="min-w-[200px]">{t("calling.notes")}</TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 w-[7rem] whitespace-nowrap px-2"
                        onClick={() => {
                          setSortBy("closingDate");
                          setSortOrder(sortBy === "closingDate" && sortOrder === "asc" ? "desc" : "asc");
                          setPage(1);
                        }}
                      >
                        {t("calling.closingDate")} {sortBy === "closingDate" && (sortOrder === "asc" ? "↑" : "↓")}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 w-[7rem] whitespace-nowrap px-2"
                        onClick={() => {
                          setSortBy("createdAt");
                          setSortOrder(sortBy === "createdAt" && sortOrder === "asc" ? "desc" : "asc");
                          setPage(1);
                        }}
                      >
                        {t("calling.createdAt")} {sortBy === "createdAt" && (sortOrder === "asc" ? "↑" : "↓")}
                      </TableHead>
                      <TableHead className="w-[120px] text-right">{t("contacts.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDeals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-10">
                          <div className="text-center space-y-3">
                            <p className="text-muted-foreground">
                              {hasExtraFilters ? t("calling.noMatches") : t("calling.noDeals")}
                            </p>
                            {hasExtraFilters ? (
                              <div className="flex justify-center">
                                <Button variant="outline" size="sm" type="button" onClick={clearClientFilters}>
                                  {t("calling.filterClear")}
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDeals.map((deal: Deal) => {
                        const ccid = dealContactId(deal);
                        return (
                        <DealRow
                            key={deal.id}
                            deal={deal}
                            contact={ccid ? contactsMap.get(ccid) : undefined}
                            stagesForPipeline={stagesForPipeline}
                            selectedStageId={selectedStageId}
                            lang={lang}
                            dateLocale={dateLocale}
                            onEmailClick={() => setEmailDealId(deal.id)}
                          onClosingDateSaved={(closingDate) => {
                            setDealOverrides((prev) => ({
                              ...prev,
                              [deal.id]: { ...(prev[deal.id] ?? {}), closingDate },
                            }));
                          }}
                          onStageChanged={(stageId) => {
                            setDealOverrides((prev) => ({
                              ...prev,
                              [deal.id]: { ...(prev[deal.id] ?? {}), stageId },
                            }));
                          }}
                          />
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {t("calling.prev")}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {t("calling.pageOf", { current: page, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  {t("calling.next")}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </>
          )}
            </>
          )}
        </div>
      </ContactsHubShell>

      <Sheet open={!!emailDealId} onOpenChange={(open) => !open && setEmailDealId(null)}>
        <SheetContent className="flex flex-col w-full sm:max-w-xl overflow-hidden p-0 gap-0">
          {emailDealId && emailDeal && (
            <EmailComposer
              embedded
              dealId={emailDealId}
                contactId={emailDeal.contactId ?? (emailDeal as any).contact_id}
                to={
                  emailContact?.email
                    ? [{ email: emailContact.email, name: emailContact.name || undefined }]
                    : []
                }
                onClose={() => setEmailDealId(null)}
                onSent={() => {
                  setEmailDealId(null);
                  qc.invalidateQueries({ queryKey: ["email-threads"] });
                }}
              />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function CallingMobileCard({
  deal,
  contact,
  onOpenDeal,
  onEmailClick,
}: {
  deal: Deal;
  contact?: Contact;
  onOpenDeal: () => void;
  onEmailClick: () => void;
}) {
  const { t } = useTranslation();
  const [sfLoading, setSfLoading] = useState(false);
  const dealId = deal.id;
  const phone = contact?.phone ?? null;
  const email = contact?.email ?? null;
  const city = (contact?.city ?? "").trim();
  const closingRaw = deal.closingDate ?? (deal as any).closing_date;
  const closingDay = dealClosingDayIso(deal);

  const handleSuperfaktura = async () => {
    setSfLoading(true);
    try {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/superfaktura-client-url`), {
        credentials: "include",
      });
      const data = await r.json().catch(() => ({}));
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } finally {
      setSfLoading(false);
    }
  };

  return (
    <div className="rounded-md border bg-card p-3 space-y-3 min-w-0">
      <div>
        <div className="font-medium text-foreground truncate">{contact?.name || "—"}</div>
        <Link
          to={callingDealPath(dealId)}
          className="block text-sm font-medium text-primary hover:underline truncate"
          onClick={onOpenDeal}
        >
          {deal.title || "—"}
        </Link>
        <div className="text-sm text-muted-foreground truncate">
          {t("deal.city")}: {city || "—"}
        </div>
      </div>
      <div className="text-xs">
        <span className="text-muted-foreground">{t("calling.closingDate")}: </span>
        {closingRaw ? (
          <span className={closingDateDisplayClass(closingDay)}>
            {new Date(closingRaw).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => {
            const p = phone?.trim();
            if (!p) return;
            logCallingTelClickAndOpenDialer(p, {
              dealId,
              contactId: contact?.id != null ? String(contact.id) : null,
              surface: "mobile",
            });
          }}
          disabled={!phone?.trim()}
        >
          <Phone className="h-4 w-4 mr-1" />
          {t("calling.call")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onEmailClick}
          disabled={!email?.trim()}
        >
          <Mail className="h-4 w-4 mr-1" />
          {t("calling.email")}
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            const cleaned = (phone ?? "").replace(/\s/g, "");
            if (cleaned) window.location.href = `sms:${cleaned}`;
          }}
          disabled={!phone?.trim()}
          title={t("calling.sms")}
          aria-label={t("calling.sms")}
        >
          <MessageSquare className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleSuperfaktura}
          disabled={sfLoading}
          title={t("calling.superfakturaTooltip")}
        >
          {sfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function DealRow({
  deal,
  contact,
  stagesForPipeline,
  selectedStageId,
  lang,
  dateLocale,
  onEmailClick,
  onClosingDateSaved,
  onStageChanged,
}: {
  deal: Deal;
  contact: Contact | undefined;
  stagesForPipeline: Stage[];
  selectedStageId: string | null;
  lang: "hu" | "sk" | "en";
  dateLocale: Locale;
  onEmailClick: () => void;
  onClosingDateSaved: (closingDate: string | null) => void;
  onStageChanged: (stageId: string) => void;
}) {
  const [superfakturaLoading, setSuperfakturaLoading] = useState(false);
  const { t } = useTranslation();
  const qc = useQueryClient();
  const dealId = deal.id;
  const currentStageId = deal.stageId ?? (deal as any).stage_id;
  const currentStageIdStr = currentStageId != null ? String(currentStageId) : null;
  const displayStage =
    stagesForPipeline.find((s) => String(s.id) === currentStageIdStr) ??
    (selectedStageId ? stagesForPipeline.find((s) => String(s.id) === String(selectedStageId)) : null);
  const contactId = deal.contactId ?? (deal as any).contact_id;

  const notesQuery = useQuery({
    queryKey: ["deal-notes", dealId],
    queryFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/notes`), { credentials: "include" });
      if (!r.ok) throw new Error("Notes betöltése sikertelen");
      const data = await r.json();
      return (data.notes ?? []) as any[];
    },
    enabled: !!dealId,
  });

  const addNote = useMutation({
    mutationFn: async (body: string) => {
      const ok = await postDealNoteWithOutbox(dealId, body);
      if (!ok) {
        notify.info(t("uploadOutbox.noteQueued"), t("uploadOutbox.noteQueuedDesc"));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-notes", dealId] });
    },
  });

  const saveClosingDate = useMutation({
    mutationFn: async (closingDate: string | null) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ closingDate }),
      });
      if (!r.ok) throw new Error(t("deal.saveFailed"));
      return r.json();
    },
    onSuccess: (_data, closingDate) => {
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      onClosingDateSaved(closingDate ?? null);
    },
  });

  const moveStage = useMutation({
    mutationFn: async (stageId: string) => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/stage?_=${Date.now()}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ stageId }),
      });
      if (!r.ok) throw new Error(t("deal.moveFailed"));
      return r.json();
    },
    onSuccess: (_data, stageId) => {
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      onStageChanged(stageId);
    },
  });

  const [noteInput, setNoteInput] = useState("");
  const [closingDateValue, setClosingDateValue] = useState<string>(
    () => (deal.closingDate ?? (deal as any).closing_date)?.toString().slice(0, 10) ?? ""
  );
  const [closingDateOpen, setClosingDateOpen] = useState(false);

  useEffect(() => {
    if (!closingDateOpen) return;
    if (closingDateValue) return;
    // Fast path: opening the picker should default to today so saving is 1 click.
    setClosingDateValue(format(new Date(), "yyyy-MM-dd"));
  }, [closingDateOpen, closingDateValue]);

  const notes = (notesQuery.data ?? []).slice().sort((a: any, b: any) => {
    const ta = String(b.updatedAt ?? b.updated_at ?? b.createdAt ?? b.created_at ?? "");
    const tb = String(a.updatedAt ?? a.updated_at ?? a.createdAt ?? a.created_at ?? "");
    return ta.localeCompare(tb);
  });
  const closingDateStr = deal.closingDate ?? (deal as any).closing_date;
  const closingDay = dealClosingDayIso(deal);

  return (
    <TableRow className="align-top">
      <TableCell className="align-top min-w-[240px] w-[260px]">
        <div className="flex flex-col gap-0.5">
          {contact && (
            <span className="font-medium text-foreground truncate">
              {contact.name || "—"}
            </span>
          )}
          <Link
            to={callingDealPath(dealId)}
            className="text-xs text-primary hover:underline truncate"
          >
            {deal.title || "—"}
          </Link>
          {contact ? (
            <span className="text-xs text-muted-foreground truncate">
              {t("deal.city")}: {(contact.city ?? "").trim() || "—"}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="align-top min-w-[150px] w-[160px]">
        {stagesForPipeline.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1 group hover:bg-muted/50 rounded px-1 py-0.5 -mx-1 text-left w-full"
                aria-label={t("calling.stageColumn")}
              >
                <span className="truncate" title={getTranslatedName(displayStage as any, lang) || displayStage?.name || undefined}>
                  {getTranslatedName(displayStage as any, lang) || displayStage?.name || "—"}
                </span>
                <ChevronDown className="h-3 w-3 flex-shrink-0 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-[280px] overflow-y-auto">
              {stagesForPipeline.map((s: Stage) => (
                <DropdownMenuItem
                  key={s.id}
                  onClick={() => {
                    if (s.id !== currentStageId) moveStage.mutate(s.id);
                  }}
                  disabled={moveStage.isPending || String(s.id) === currentStageIdStr}
                >
                  {String(s.id) === currentStageIdStr && <span className="mr-2">✓</span>}
                  {getTranslatedName(s as any, lang) || s.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
      <TableCell className="align-top min-w-[220px]">
        <div className="space-y-2">
          {notes.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-2 text-sm pr-1">
              {notes.map((n: any) => (
                <DealNoteInlineItem key={n.id} dealId={dealId} note={n} dateLocale={dateLocale} />
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end flex-shrink-0">
            <textarea
              placeholder={t("deal.newNotePlaceholder")}
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              className="flex-1 min-h-[52px] rounded-md border bg-background px-2 py-1.5 text-sm resize-y"
              rows={2}
            />
            <Button
              size="sm"
              className="flex-shrink-0"
              onClick={() => {
                if (noteInput.trim()) {
                  addNote.mutate(noteInput.trim());
                  setNoteInput("");
                }
              }}
              disabled={!noteInput.trim() || addNote.isPending}
            >
              {addNote.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("deal.addActivity")}
            </Button>
          </div>
        </div>
      </TableCell>
      <TableCell className="align-top w-[7rem] whitespace-nowrap px-2 text-xs">
        <Popover open={closingDateOpen} onOpenChange={setClosingDateOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1 group hover:bg-muted/50 rounded px-1 py-0.5 -mx-1"
              aria-label={t("deal.edit")}
            >
              <span
                className={cn(
                  closingDateStr ? closingDateDisplayClass(closingDay) : "text-muted-foreground",
                )}
              >
                {closingDateStr
                  ? new Date(closingDateStr).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "—"}
              </span>
              <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
            side="left"
            sideOffset={12}
          >
            <ClosingDatePickerContent
              value={closingDateValue}
              onChange={setClosingDateValue}
              onCancel={() => setClosingDateOpen(false)}
              onSave={() => {
                saveClosingDate.mutate(closingDateValue || null, {
                  onSuccess: () => setClosingDateOpen(false),
                });
              }}
              saving={saveClosingDate.isPending}
              locale={dateLocale}
            />
          </PopoverContent>
        </Popover>
      </TableCell>
      <TableCell className="align-top w-[7rem] whitespace-nowrap px-2 text-xs">
        {deal.createdAt
          ? new Date(deal.createdAt).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "—"}
      </TableCell>
      <TableCell className="align-top w-[120px] text-right">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            asChild
            title={t("calling.openDeal")}
          >
            <a href={callingDealPath(dealId)} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
          {contact?.phone && (
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => {
                const raw = (contact?.phone ?? "").replace(/\s/g, "");
                if (raw) {
                  logCallingTelClickAndOpenDialer(raw, {
                    dealId,
                    contactId: contact?.id != null ? String(contact.id) : null,
                    surface: "table",
                  });
                }
              }}
              title={t("calling.call")}
            >
              <Phone className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={onEmailClick}
            title={t("calling.email")}
          >
            <Mail className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            disabled={superfakturaLoading}
            onClick={async () => {
              setSuperfakturaLoading(true);
              try {
                const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/superfaktura-client-url`), {
                  credentials: "include",
                });
                const data = await r.json().catch(() => ({}));
                if (data?.url) {
                  window.open(data.url, "_blank", "noopener,noreferrer");
                }
              } finally {
                setSuperfakturaLoading(false);
              }
            }}
            title={t("calling.superfakturaTooltip")}
          >
            {superfakturaLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function noteInstantIso(note: {
  updatedAt?: string;
  updated_at?: string;
  createdAt?: string;
  created_at?: string;
}): string {
  return String(note.updatedAt ?? note.updated_at ?? note.createdAt ?? note.created_at ?? "");
}

function formatNoteWhen(iso: string, dateLocale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, "PPpp", { locale: dateLocale });
}

function DealNoteInlineItem({
  dealId,
  note,
  dateLocale,
}: {
  dealId: string;
  note: any;
  dateLocale: Locale;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note.body ?? "");
  const whenLabel = formatNoteWhen(noteInstantIso(note), dateLocale);

  const upd = useMutation({
    mutationFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/notes`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ noteId: note.id, body: value }),
      });
      if (!r.ok) throw new Error("Jegyzet módosítás hiba");
      return r.json();
    },
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["deal-notes", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });

  const del = useMutation({
    mutationFn: async () => {
      const r = await fetch(apiUrl(`/api/crm/deals/${dealId}/notes`), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ noteId: note.id }),
      });
      if (!r.ok) throw new Error("Jegyzet törlés hiba");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-notes", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });

  if (editing) {
    return (
      <div className="border-b border-border/50 pb-2 last:border-0 last:pb-0">
        <div className="text-xs text-muted-foreground mb-1 flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">
            {note.editorName || note.authorName || note.authorEmail || "—"}
          </span>
          <span>•</span>
          <span>{whenLabel}</span>
        </div>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full rounded-md border bg-background px-2 py-1.5 text-sm min-h-[80px] resize-y"
          autoFocus
        />
        <div className="flex gap-2 mt-2">
          <Button size="sm" onClick={() => upd.mutate()} disabled={upd.isPending || !value.trim()}>
            {upd.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditing(false);
              setValue(note.body ?? "");
            }}
          >
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-border/50 pb-2 last:border-0 last:pb-0">
      <div className="text-xs text-muted-foreground mb-0.5 flex items-center gap-2">
        <span>{whenLabel}</span>
        <span className="ml-auto flex gap-1">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="p-1.5 rounded hover:bg-muted text-foreground/70 hover:text-foreground"
            title={t("deal.editLabel")}
            aria-label={t("deal.editLabel")}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirm(t("deal.noteDeleteConfirm"))) del.mutate();
            }}
            className="p-1.5 rounded hover:bg-destructive/20 text-foreground/70 hover:text-destructive"
            title={t("deal.deleteLabel")}
            aria-label={t("deal.deleteLabel")}
            disabled={del.isPending}
          >
            {del.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </button>
        </span>
      </div>
      <p className="whitespace-pre-wrap break-words text-foreground">{note.body}</p>
    </div>
  );
}
